// src/components/SendInvoiceModal.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiX, FiSend, FiAlertTriangle, FiLoader, FiInfo } from 'react-icons/fi';
import * as invoiceSvc from '../services/invoiceService';

/* -------------------------------- utils -------------------------------- */

const stripUndefined = (obj = {}) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null));

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

function parseEmails(input) {
  if (!input) return [];
  return String(input)
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function useEscapeToClose(enabled, onClose) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, onClose]);
}

function useFocusOnOpen(isOpen, ref) {
  useEffect(() => {
    if (isOpen && ref?.current) {
      const t = setTimeout(() => ref.current?.focus?.(), 0);
      return () => clearTimeout(t);
    }
  }, [isOpen, ref]);
}

const normalizeStatus = (s) => String(s || '').trim().toLowerCase();

/* ---------------------------- small UI helpers --------------------------- */

const Switch = ({ checked, onChange, disabled, id }) => (
  <label
    htmlFor={id}
    className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
  >
    <input
      type="checkbox"
      id={id}
      className="sr-only peer"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <div className="w-11 h-6 bg-neutral-200 dark:bg-neutral-700 rounded-full peer peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-400 dark:peer-focus:ring-indigo-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 dark:after:border-neutral-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-neutral-600 peer-checked:bg-indigo-600" />
  </label>
);

const Field = ({ label, children, hint, error }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">{label}</label>
    <div className="mt-2">{children}</div>
    {hint && !error && <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{hint}</p>}
    {error && (
      <p className="mt-1 text-xs text-rose-600 inline-flex items-center gap-1">
        <FiAlertTriangle /> {error}
      </p>
    )}
  </div>
);

/* -------------------------------- component ----------------------------- */

export default function SendInvoiceModal({
  isOpen,
  onClose,
  invoice,
  onSent,
  lastSentAt,         // <- parent must provide for the “3h ago / within window” chip
  loadPreview,        // optional: server preview endpoint
}) {
  const toInputRef = useRef(null);

  // robust id extraction
  const invoiceId = useMemo(
    () =>
      invoice?.id || invoice?._id || invoice?.invoiceId
        ? String(invoice.id || invoice._id || invoice.invoiceId)
        : '',
    [invoice]
  );

  // states that require force override (similar to Quote’s accepted)
  const status = normalizeStatus(invoice?.lifecycleStatus || invoice?.status);
  const FORCE_STATES = useMemo(
    () => new Set(['paid', 'paid_partial', 'void', 'voided', 'cancelled', 'canceled', 'archived', 'written_off', 'write_off']),
    []
  );
  const requiresForce = FORCE_STATES.has(status);

  const clientEmail =
    invoice?.client?.email ||
    invoice?.clientEmail ||
    invoice?.clientSnapshot?.email ||
    invoice?.clientSnapshot?.billingEmail ||
    '';
  const clientName =
    invoice?.client?.name || invoice?.clientName || invoice?.clientSnapshot?.name || 'Client';
  const invoiceNumber = invoice?.invoiceNumber || invoice?.number || invoiceId || '';

  // state
  const [toRaw, setToRaw] = useState(clientEmail);
  const [ccRaw, setCcRaw] = useState('');
  const [message, setMessage] = useState('');
  const [force, setForce] = useState(false);
  const [attachPdf, setAttachPdf] = useState(false);

  const [subjectPreview, setSubjectPreview] = useState('');
  const [pdfPeek, setPdfPeek] = useState({ exists: false, filename: null, size: null, canAttach: true });

  const [preparing, setPreparing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [pdfEnsuring, setPdfEnsuring] = useState(false);

  const [preview, setPreview] = useState(null);
  const [apiWarning, setApiWarning] = useState(null);

  const toList = useMemo(() => parseEmails(toRaw), [toRaw]);
  const ccList = useMemo(() => parseEmails(ccRaw), [ccRaw]);

  const invalidTo = useMemo(() => toList.filter((e) => !emailRegex.test(e)), [toList]);
  const invalidCc = useMemo(() => ccList.filter((e) => !emailRegex.test(e)), [ccList]);

  const anyBusy = preparing || submitting || previewBusy;

  useEscapeToClose(isOpen && !anyBusy, onClose);
  useFocusOnOpen(isOpen, toInputRef);

  const relativeLastSent = useMemo(() => {
    if (!lastSentAt) return null;
    try {
      const then = new Date(lastSentAt).getTime();
      const now = Date.now();
      const diffSec = Math.max(0, Math.round((now - then) / 1000));
      if (diffSec < 60) return `${diffSec}s ago`;
      const diffMin = Math.round(diffSec / 60);
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffH = Math.round(diffMin / 60);
      if (diffH < 24) return `${diffH}h ago`;
      const diffD = Math.round(diffH / 24);
      return `${diffD}d ago`;
    } catch {
      return null;
    }
  }, [lastSentAt]);

  const inIdemWindow = useMemo(() => {
    if (!lastSentAt) return false;
    const then = new Date(lastSentAt).getTime();
    return Date.now() - then < 15 * 60 * 1000; // 15 minutes
  }, [lastSentAt]);

  // Ensure/refresh PDF meta — return meta to avoid stale-state races
  const refreshPdfPeek = useCallback(
    async (opt = { force: false }) => {
      if (!invoiceId || typeof invoiceSvc.ensureStoredInvoicePdf !== 'function') return null;
      setPdfEnsuring(true);
      try {
        const meta = await invoiceSvc.ensureStoredInvoicePdf(invoiceId, opt);
        const exists = !!(meta?.fileUrl || meta?.fileKey || meta?.exists);
        const next = {
          exists,
          filename: meta?.fileName || meta?.filename || null,
          size: Number(meta?.size || 0) || null,
          canAttach: meta?.canAttach !== false, // default true
        };
        setPdfPeek(next);
        return next;
      } catch (e) {
        const next = { ...pdfPeek, canAttach: false };
        setPdfPeek(next);
        toast.warn(e?.message || 'Could not prepare PDF. You can still send without attachment.');
        return next;
      } finally {
        setPdfEnsuring(false);
      }
    },
    [invoiceId, pdfPeek]
  );

  // reset & prefill on open (mirrors Quote)
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!isOpen || !invoiceId) return;

      setToRaw(clientEmail || '');
      setCcRaw('');
      setMessage('');
      setForce(requiresForce); // default ON if required
      setAttachPdf(false);
      setSubjectPreview('');
      setPdfPeek({ exists: false, filename: null, size: null, canAttach: true });
      setApiWarning(null);
      setPreview(null);

      if (typeof invoiceSvc.prepareSendInvoice === 'function') {
        setPreparing(true);
        try {
          const pre = await invoiceSvc.prepareSendInvoice(invoiceId);
          if (cancelled) return;

          const toPrefill =
            Array.isArray(pre?.to) ? pre.to.join(', ')
            : typeof pre?.to === 'string' ? pre.to
            : Array.isArray(pre?.recipients) ? pre.recipients.join(', ')
            : clientEmail || '';

          const ccPrefill =
            Array.isArray(pre?.cc) ? pre.cc.join(', ')
            : typeof pre?.cc === 'string' ? pre.cc
            : '';

          setToRaw(toPrefill);
          setCcRaw(ccPrefill);
          setMessage(pre?.shortMessage || '');
          setSubjectPreview(pre?.subject || '');

          if (pre?.pdf) {
            setPdfPeek({
              exists: !!pre.pdf.exists,
              filename: pre.pdf.filename || null,
              size: Number(pre.pdf.size || 0) || null,
              canAttach: pre.pdf.canAttach !== false,
            });
          }
        } catch (e) {
          toast.warn(e?.message || 'Unable to prefill from server. You can still send.');
        } finally {
          setPreparing(false);
        }
      }

      // Independently ensure/refresh stored PDF so we can show filename (parity with Quote)
      await refreshPdfPeek({ force: false });
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, invoiceId, clientEmail, requiresForce]);

  const canSubmit =
    toList.length > 0 &&
    invalidTo.length === 0 &&
    invalidCc.length === 0 &&
    !submitting &&
    !preparing;

  const buildPayload = useCallback(() => {
    // Always include force=true if required so backend override is explicit.
    const effectiveForce = requiresForce ? true : !!force;
    return stripUndefined({
      to: toList.length ? toList : undefined,
      cc: ccList.length ? ccList : undefined,
      message: message?.trim() ? message.trim() : undefined,
      attachPdf: attachPdf || undefined,
      force: effectiveForce ? true : undefined,
    });
  }, [toList, ccList, message, attachPdf, force, requiresForce]);

  const handlePreview = useCallback(async () => {
    if (!loadPreview || !invoiceId) return;
    setPreviewBusy(true);
    setApiWarning(null);
    try {
      const payload = buildPayload();
      const data = await loadPreview(invoiceId, payload);
      setPreview(data || null);
    } catch (e) {
      toast.error(e?.message || 'Unable to load preview.');
    } finally {
      setPreviewBusy(false);
    }
  }, [loadPreview, invoiceId, buildPayload]);

  const handleAttachToggle = useCallback(
    async (checked) => {
      if (!checked) {
        setAttachPdf(false);
        return;
      }
      const meta = await refreshPdfPeek({ force: true });
      const exists = !!(meta?.exists || meta?.fileUrl || meta?.fileKey);
      if (!exists) {
        toast.warn('PDF could not be prepared. Sending without attachment.');
        setAttachPdf(false);
        return;
      }
      if (meta?.canAttach === false) {
        toast.warn('Attachment disabled by policy. Sending without attachment.');
        setAttachPdf(false);
        return;
      }
      setAttachPdf(true);
    },
    [refreshPdfPeek]
  );

  const handleSubmit = useCallback(async () => {
    if (!invoiceId || !canSubmit) return;

    setSubmitting(true);
    setApiWarning(null);

    try {
      const payload = buildPayload();
      const result = await invoiceSvc.sendInvoice(invoiceId, payload);

      if (result.status === 'duplicate') {
        if (requiresForce) {
          toast.info('This invoice is locked. Re-sending requires Force Resend.');
        } else {
          toast.info('Already sent recently (idempotent). Enable “Force resend” to override.');
        }
        onSent?.(result);
        return; // keep modal open so user can toggle Force
      }

      if (result.rateLimited) {
        setApiWarning({
          title: 'Rate limit reached',
          detail: 'Please wait a bit before trying again.',
        });
        toast.warn('Rate limit reached.');
        onSent?.(result);
        return;
      }

      if (result.queued) {
        toast.success('Queued for delivery.');
      } else {
        toast.success('Invoice sent.');
      }

      onSent?.(result);
      onClose?.();
    } catch (e) {
      const status = e?.response?.status || e?.status;
      const data = e?.response?.data || e?.data;

      if (status === 429) {
        setApiWarning({
          title: 'Rate limit reached',
          detail: data?.message || 'You have reached the sending limit. Please try again later.',
        });
        toast.warn('Rate limit reached.');
      } else if (status === 400 && data?.suppression) {
        setApiWarning({
          title: 'Address suppressed',
          detail:
            data?.suppression?.reason ||
            'The recipient is suppressed due to previous bounces or complaints.',
        });
        toast.warn('Recipient is suppressed.');
      } else if (status === 409) {
        if (requiresForce) {
          toast.info('This invoice is locked. Re-sending requires Force Resend.');
        } else {
          toast.info('Already sent recently (idempotent). Enable “Force resend” to override.');
        }
      } else {
        toast.error(e?.message || 'Failed to send invoice.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [invoiceId, canSubmit, buildPayload, onSent, onClose, requiresForce]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      onKeyDown={(e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canSubmit) {
          e.preventDefault();
          handleSubmit();
        }
      }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-300 ease-in-out"
        onClick={preparing || submitting || previewBusy ? undefined : onClose}
        aria-hidden="true"
      />
      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-invoice-title"
        className="absolute inset-0 flex items-end sm:items-center justify-center p-4"
      >
        <div className="w-full sm:max-w-3xl rounded-2xl bg-white dark:bg-neutral-900 shadow-2xl overflow-hidden dark:border dark:border-neutral-700/80">
          {/* Header */}
          <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between bg-neutral-50 dark:bg-neutral-800/50">
            <div className="min-w-0">
              <h3 id="send-invoice-title" className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                Send Invoice
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 truncate">
                {invoiceNumber ? `Invoice ${invoiceNumber}` : 'This invoice'} • {clientName}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={preparing || submitting || previewBusy}
              className="p-2 rounded-full hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-500 dark:text-neutral-400 disabled:opacity-50 transition-colors"
              aria-label="Close"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-6 space-y-5 relative max-h-[70vh] overflow-y-auto">
            {preparing && (
              <div className="absolute inset-0 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm z-10 pointer-events-none flex items-center justify-center">
                <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-300">
                  <FiLoader className="animate-spin" />
                  Loading…
                </div>
              </div>
            )}

            {apiWarning && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 dark:border-amber-700/50 px-4 py-3 text-sm">
                <div className="flex items-start gap-3">
                  <FiAlertTriangle className="mt-0.5 shrink-0 text-amber-500" />
                  <div>
                    <div className="font-medium dark:text-amber-100">{apiWarning.title}</div>
                    <div className="mt-0.5">{apiWarning.detail}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Subject from prepare */}
            {subjectPreview ? (
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">Subject</label>
                <p className="mt-2 text-sm text-neutral-800 dark:text-neutral-200 truncate">
                  {subjectPreview}
                </p>
              </div>
            ) : null}

            {/* fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="To"
                hint="Separate multiple emails with commas or spaces."
                error={
                  invalidTo.length
                    ? `Invalid email${invalidTo.length > 1 ? 's' : ''}: ${invalidTo.join(', ')}`
                    : undefined
                }
              >
                <input
                  ref={toInputRef}
                  type="text"
                  value={toRaw}
                  onChange={(e) => setToRaw(e.target.value)}
                  className="block w-full rounded-lg border-0 bg-neutral-100 dark:bg-neutral-800 px-3.5 py-2.5 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 transition"
                  placeholder="client@example.com"
                  disabled={preparing || submitting}
                  autoComplete="email"
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {toList.slice(0, 6).map((e) => (
                    <span
                      key={e}
                      className="inline-flex items-center rounded-md bg-indigo-100/80 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 px-2 py-1 text-xs font-medium"
                    >
                      {e}
                    </span>
                  ))}
                  {toList.length > 6 && (
                    <span className="text-xs text-neutral-500 dark:text-neutral-400 py-1">
                      +{toList.length - 6} more
                    </span>
                  )}
                </div>
              </Field>

              <Field
                label="Cc (optional)"
                hint="Separate multiple emails with commas or spaces."
                error={
                  invalidCc.length
                    ? `Invalid email${invalidCc.length > 1 ? 's' : ''}: ${invalidCc.join(', ')}`
                    : undefined
                }
              >
                <input
                  type="text"
                  value={ccRaw}
                  onChange={(e) => setCcRaw(e.target.value)}
                  className="block w-full rounded-lg border-0 bg-neutral-100 dark:bg-neutral-800 px-3.5 py-2.5 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 transition"
                  placeholder="team@example.com"
                  disabled={preparing || submitting}
                  autoComplete="email"
                />
              </Field>
            </div>

            <Field label="Message (optional)" hint="Short note included at the top of the email.">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="block w-full rounded-lg border-0 bg-neutral-100 dark:bg-neutral-800 px-3.5 py-2.5 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 min-h-[96px] transition"
                placeholder="Hi! Here is your invoice. You can pay securely via the link inside."
                disabled={preparing || submitting}
              />
            </Field>

            {/* toggles */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 pt-1">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <label htmlFor="attach-pdf-toggle" className="font-medium text-neutral-800 dark:text-neutral-100 cursor-pointer">
                    Attach PDF
                  </label>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                    {pdfEnsuring && <FiLoader className="inline animate-spin mr-1" />}
                    {pdfPeek?.exists && pdfPeek?.filename ? (
                      <span title={pdfPeek.filename}>{pdfPeek.filename}</span>
                    ) : (
                      'Attach a freshly rendered PDF.'
                    )}
                    {pdfPeek?.canAttach === false && (
                      <span className="ml-1.5 text-amber-600 dark:text-amber-500">(Disabled)</span>
                    )}
                  </div>
                </div>
                <Switch
                  id="attach-pdf-toggle"
                  checked={attachPdf}
                  onChange={(e) => handleAttachToggle(e.target.checked)}
                  disabled={preparing || submitting || pdfPeek?.canAttach === false || pdfEnsuring}
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <label htmlFor="force-resend-toggle" className="font-medium text-neutral-800 dark:text-neutral-100 cursor-pointer">
                    Force Resend {requiresForce && <span className="ml-1 rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-700">Required</span>}
                  </label>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                    {requiresForce
                      ? `Invoice is ${status || 'locked'}; override is required.`
                      : `Override 15min check${relativeLastSent ? ` (${relativeLastSent})` : ''}.`}
                    {!requiresForce && inIdemWindow && (
                      <span className="ml-1.5 text-amber-600">(within window)</span>
                    )}
                  </div>
                </div>
                <Switch
                  id="force-resend-toggle"
                  checked={requiresForce ? true : force}
                  onChange={(e) => (requiresForce ? null : setForce(e.target.checked))}
                  disabled={preparing || submitting || requiresForce}
                />
              </div>
            </div>

            {typeof loadPreview === 'function' && (
              <div className="rounded-lg border border-neutral-200 dark:border-neutral-700">
                <div className="px-4 py-2 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between bg-neutral-50 dark:bg-neutral-800/50">
                  <div className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Preview</div>
                  <button
                    type="button"
                    onClick={handlePreview}
                    disabled={previewBusy || preparing}
                    className="h-8 px-3 rounded-lg text-sm bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-600 disabled:opacity-50 inline-flex items-center gap-2 transition-colors"
                  >
                    {previewBusy ? <FiLoader className="animate-spin" /> : null}
                    Refresh
                  </button>
                </div>
                <div className="p-4">
                  {preview ? (
                    <div className="space-y-3">
                      {preview.subject && (
                        <div>
                          <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Subject</div>
                          <div className="text-sm text-neutral-900 dark:text-neutral-100">{preview.subject}</div>
                        </div>
                      )}
                      {preview.html ? (
                        <div>
                          <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">HTML</div>
                          <div
                            className="prose prose-sm max-w-none dark:prose-invert"
                            dangerouslySetInnerHTML={{ __html: preview.html }}
                          />
                        </div>
                      ) : preview.text ? (
                        <pre className="text-xs bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-lg p-3 whitespace-pre-wrap">
                          {preview.text}
                        </pre>
                      ) : (
                        <div className="text-sm text-neutral-500 dark:text-neutral-400">No preview available.</div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-neutral-500 dark:text-neutral-400">
                      Click <span className="font-medium">Refresh</span> to fetch a preview.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={preparing || submitting}
              className="h-10 px-4 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="h-10 px-4 rounded-lg text-white shadow-sm flex items-center gap-2 disabled:opacity-50 bg-indigo-600 hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
            >
              {submitting ? <FiLoader className="animate-spin" /> : <FiSend size={16} />}
              {attachPdf ? 'Send & Attach' : 'Send Invoice'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
